import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';

/**
 * Initialize Sentry error tracking.
 * Works in both web browsers and Capacitor WebViews (Android/iOS).
 * Only active when VITE_SENTRY_DSN is set — safe to omit in development.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    return;
  }

  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform(); // 'android' | 'ios' | 'web'

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
    maxBreadcrumbs: 50,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    // Trace API calls to our backend (not just same-origin, which is localhost in Capacitor)
    tracePropagationTargets: [
      /^https?:\/\/localhost/,
      /poweresh\.online/,
      /eveebuddy\.com/,
    ],
    // Capacitor WebView URLs (http://localhost, https://localhost) are not useful as
    // transaction names — beforeSendTransaction strips the origin so we get clean route paths.
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value ?? '';
      // Filter out browser/extension noise that isn't actionable
      if (
        message.match(/ResizeObserver loop/i) ||
        message.match(/Loading chunk \d+ failed/i) ||
        message.match(/ChunkLoadError/i) ||
        message.match(/Failed to fetch dynamically imported module/i) ||
        message.match(/Extension context invalidated/i)
      ) {
        return null;
      }
      return event;
    },
    beforeSendTransaction(event) {
      if (isNative && event.transaction) {
        event.transaction = event.transaction.replace(/^https?:\/\/localhost/, '');
      }
      return event;
    },
    initialScope: {
      tags: {
        platform,
        isNative: String(isNative),
      },
    },
  });
}

/**
 * Set the current user on Sentry scope (call after login).
 */
export function setSentryUser(user: { id: string; email?: string | null; firstName?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email ?? undefined,
    username: user.firstName,
  });
}

/**
 * Clear user from Sentry scope (call on logout).
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Add a breadcrumb for debugging context on future errors.
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, any>,
  level: Sentry.SeverityLevel = 'info',
) {
  Sentry.addBreadcrumb({ category, message, data, level });
}

/**
 * Set charging session context on the Sentry scope.
 */
export function setChargingContext(session: { transactionId: string; chargePointId: string; connectorId: string } | null) {
  Sentry.setContext('charging_session', session);
}

/**
 * Set payment context on the Sentry scope.
 */
export function setPaymentContext(payment: { orderId?: string; amount?: number; status?: string } | null) {
  Sentry.setContext('payment', payment);
}
